import React, { useEffect } from 'react';

export interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'default';
  loading?: boolean;
  /** Contenido custom entre la descripción y los botones (ej. un formulario o tabla). */
  children?: React.ReactNode;
  /** Si el botón confirmar debe estar deshabilitado (validación incompleta). */
  confirmDisabled?: boolean;
  /** Mensaje de error opcional sobre los botones. */
  error?: string;
  /** Modal más ancho (480px en vez de 340px) — útil cuando hay contenido custom. */
  wide?: boolean;
  /** Alineación del texto del cuerpo. Por defecto centrado. */
  align?: 'left' | 'center';
  /** data-testid del botón confirmar (opcional, para tests). */
  confirmTestId?: string;
  /** data-testid del botón cancelar (opcional, para tests). */
  cancelTestId?: string;
  /** data-testid del mensaje de error (opcional, para tests). */
  errorTestId?: string;
}

const variantColors: Record<string, { bg: string; color: string; btn: string }> = {
  danger:  { bg: '#FEE2E2', color: '#DC2626', btn: '#DC2626' },
  warning: { bg: '#FEF3C7', color: '#D97706', btn: '#D97706' },
  default: { bg: '#EEF2FF', color: '#0063EA', btn: '#001A70' },
};

const TrashIcon = () => (
  <svg width="22" height="22" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const WarningIcon = () => (
  <svg width="22" height="22" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
      d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
  </svg>
);

const InfoIcon = () => (
  <svg width="22" height="22" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
      d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
  </svg>
);

const variantIcons: Record<string, React.FC> = {
  danger: TrashIcon,
  warning: WarningIcon,
  default: InfoIcon,
};

export const ConfirmModal = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Eliminar',
  cancelLabel = 'Cancelar',
  variant = 'danger',
  loading = false,
  children,
  confirmDisabled = false,
  error,
  wide = false,
  align = 'center',
  confirmTestId,
  cancelTestId,
  errorTestId,
}: ConfirmModalProps) => {
  const colors = variantColors[variant] ?? variantColors.default;
  const VariantIcon = variantIcons[variant] ?? InfoIcon;
  const confirmIsDisabled = loading || confirmDisabled;

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 300,
        background: 'rgba(0,26,112,0.15)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 22,
          padding: '2.2rem',
          width: wide ? 480 : 340,
          maxWidth: '92vw',
          textAlign: align,
          boxShadow: '0 25px 60px rgba(0,26,112,0.15)',
        }}
      >
        {/* Icon */}
        <div style={{
          width: 52, height: 52,
          background: colors.bg,
          borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: align === 'center' ? '0 auto 0.9rem' : '0 0 0.9rem',
          color: colors.color,
        }}>
          <VariantIcon />
        </div>
        <h3 style={{
          fontFamily: 'Nunito, sans-serif', fontSize: '1.15rem', fontWeight: 900,
          color: '#001A70', marginBottom: 5,
        }}>{title}</h3>
        {description && (
          <p style={{ fontSize: '0.82rem', color: '#94A3B8', fontWeight: 600, marginBottom: children ? '1.2rem' : '1.5rem' }}>
            {description}
          </p>
        )}
        {children && <div style={{ marginBottom: '1rem' }}>{children}</div>}
        {error && (
          <p data-testid={errorTestId} style={{ color: '#DC2626', fontSize: '0.78rem', marginBottom: '0.8rem', fontWeight: 700, textAlign: align }}>
            {error}
          </p>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9, marginTop: description || children ? 0 : '1.5rem' }}>
          <button
            data-testid={cancelTestId}
            onClick={onClose}
            disabled={loading}
            style={{
              padding: 9, background: '#F1F5F9', color: '#64748B', border: 'none',
              borderRadius: 11, fontWeight: 800, fontSize: 11, textTransform: 'uppercase',
              cursor: 'pointer', fontFamily: 'Nunito Sans, sans-serif',
            }}
          >
            {cancelLabel}
          </button>
          <button
            data-testid={confirmTestId}
            onClick={onConfirm}
            disabled={confirmIsDisabled}
            style={{
              padding: 9,
              background: confirmIsDisabled ? '#FCA5A5' : colors.btn,
              color: 'white', border: 'none',
              borderRadius: 11, fontWeight: 800, fontSize: 11, textTransform: 'uppercase',
              cursor: confirmIsDisabled ? 'not-allowed' : 'pointer', fontFamily: 'Nunito Sans, sans-serif',
              opacity: loading ? 0.7 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'background 0.15s',
            }}
          >
            {loading && (
              <svg
                width="13" height="13" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                style={{ animation: 'spin 0.7s linear infinite', flexShrink: 0 }}
              >
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
              </svg>
            )}
            {confirmLabel}
          </button>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    </div>
  );
};
